import React, { useEffect, useState, useCallback } from 'react';
import api from '../utils/api';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { X, Loader2, UserPlus, MapPin, Users, Star, Send, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import BadgeRow from './BadgeRow';
import CertificationList from './CertificationList';
import LifecycleTimeline from './LifecycleTimeline';
import { useTalentProfileModal } from './TalentProfileModalProvider';
import { useInviteTalentToShift } from './InviteTalentToShiftProvider';
import { useAuth } from '../context/AuthContext';
import AddToInviteCartButton from './AddToInviteCartButton';
import HaloRing from './HaloRing';
import GgfBadge from './GgfBadge';

/**
 * ApplicantDrawer
 *
 * Right-side drawer that lists every PENDING applicant for a shift.
 * Opens from any shift-card `{N} applicants` chip via the parent
 * component (AdminShiftDetail, AdminShifts, Calendar). Each row shows
 * name, city/state, role, status, reliability score, and a Quick Book
 * action that triggers `POST /admin/shifts/{sid}/assign`.
 *
 * Iter-200 hardening — every field rendered from the payload is now
 * null-safe (optional chaining + fallbacks) so an unexpected null in
 * a single applicant row can never white-screen the entire admin
 * shift detail page (recurring React rendering pattern).
 */

// Iter-200 — local lightweight error fallback so the parent's
// `<ErrorBoundary>` can render an inline drawer error instead of a
// full-screen crash if the row map ever throws on a future payload.
const DrawerInlineError = ({ reset, onClose }) => (
  <div
    className="fixed inset-0 z-50 flex"
    role="dialog"
    aria-modal="true"
    data-testid="applicant-drawer-error"
  >
    <div className="flex-1 bg-black/40 backdrop-blur-sm" onClick={onClose} />
    <aside className="w-full sm:max-w-md bg-white shadow-2xl border-l border-gray-200 flex flex-col">
      <header className="px-5 py-4 border-b border-gray-200 flex items-center justify-between">
        <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
          <AlertTriangle className="w-5 h-5 text-amber-600" /> Applicants
        </h2>
        <Button variant="ghost" size="icon" onClick={onClose}>
          <X className="w-5 h-5" />
        </Button>
      </header>
      <div className="flex-1 flex flex-col items-center justify-center text-center px-6 gap-3">
        <AlertTriangle className="w-10 h-10 text-amber-500" />
        <p className="text-sm text-gray-700">
          We hit an unexpected issue rendering this applicant list. Our team has been notified.
        </p>
        <Button onClick={reset} variant="outline" data-testid="applicant-drawer-retry-btn">
          Try again
        </Button>
      </div>
    </aside>
  </div>
);

const ApplicantDrawer = ({ shiftId, open, onClose, onBooked }) => {
  const [loading, setLoading] = useState(false);
  const [applicants, setApplicants] = useState([]);
  const [bookingId, setBookingId] = useState(null);
  const [sortNearest, setSortNearest] = useState(false);
  const [radiusFilter, setRadiusFilter] = useState(null); // null | 5 | 10 | 15 | 25 | 50
  const { open: openProfile } = useTalentProfileModal();
  const { open: openInviteToShift } = useInviteTalentToShift();
  const { user } = useAuth();
  const isAdmin = (user?.role || '').toLowerCase() === 'admin';
  // Iter-254 — GGF visibility context. Drawer is rendered for both admins
  // and clients (shift-detail page). For non-admin / non-GGF clients we
  // hide the cert badge to avoid leaking client-specific certification.
  const isGgfClient = /\b(guardian garage floors|ggf)\b/i.test(`${user?.company_name || ''} ${user?.name || ''}`);

  const load = useCallback(async () => {
    if (!shiftId) return;
    setLoading(true);
    try {
      const res = await api.get(`/admin/shifts/${shiftId}/applicants`);
      const list = Array.isArray(res?.data?.applicants) ? res.data.applicants : [];
      setApplicants(list);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load applicants');
      setApplicants([]);
    } finally {
      setLoading(false);
    }
  }, [shiftId]);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      // Defer to topmost Radix Dialog (e.g. TalentProfileModal) if open.
      if (document.querySelector('[role="dialog"][data-state="open"]')) return;
      onClose?.();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  const quickBook = async (talentId) => {
    if (!talentId) return;
    setBookingId(talentId);
    try {
      await api.post(
        `/admin/shifts/${shiftId}/assign`,
        null,
        { params: { talent_id: talentId, assignment_type: 'primary' } },
      );
      toast.success('Talent booked as primary');
      await load();
      onBooked?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to book talent');
    } finally {
      setBookingId(null);
    }
  };

  if (!open) return null;

  // Iter-200 — defensive: ensure we always have an array
  const safeList = Array.isArray(applicants) ? applicants : [];
  const anyDistance = safeList.some(a => a && a.distance_available && a.distance_miles != null);
  const displayList = (() => {
    let list = safeList.slice();
    if (radiusFilter) {
      list = list.filter(a => a && a.distance_available && a.distance_miles != null && a.distance_miles <= radiusFilter);
    }
    if (sortNearest) {
      list.sort((x, y) => (x.distance_miles == null ? 1e9 : x.distance_miles) - (y.distance_miles == null ? 1e9 : y.distance_miles));
    }
    return list;
  })();

  return (
    <div
      className="fixed inset-0 z-50 flex"
      role="dialog"
      aria-modal="true"
      data-testid="applicant-drawer"
      // Iter-P0-20260801 item #10 — lock body scroll while drawer open but
      // allow scrolling *within* the panel on iOS Safari & Instagram in-app.
      style={{ height: '100dvh' }}
    >
      {/* Backdrop */}
      <div
        className="flex-1 bg-black/40 backdrop-blur-sm"
        onClick={onClose}
        data-testid="applicant-drawer-backdrop"
      />

      {/* Panel */}
      <aside
        className="w-full sm:max-w-md bg-white shadow-2xl border-l border-gray-200 flex flex-col h-full max-h-full min-h-0"
      >
        <header className="px-5 py-4 border-b border-gray-200 flex items-center justify-between shrink-0">
          <div className="min-w-0">
            <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
              <Users className="w-5 h-5 text-indigo-600" /> Applicants
            </h2>
            <p className="text-xs text-gray-500 mt-0.5">
              Pending applications · click Quick Book to assign as primary
            </p>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose} data-testid="close-applicant-drawer-btn">
            <X className="w-5 h-5" />
          </Button>
        </header>

        {/* Distance sort + radius filter (venue-based) */}
        {anyDistance && (
          <div className="px-4 py-2 border-b border-gray-100 bg-slate-50/60 flex items-center gap-2 flex-wrap text-xs shrink-0" data-testid="applicant-distance-toolbar">
            <button
              type="button"
              onClick={() => setSortNearest(v => !v)}
              className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full border font-semibold transition ${sortNearest ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300'}`}
              data-testid="applicant-sort-nearest"
            >
              <MapPin className="w-3 h-3" /> Nearest first
            </button>
            <span className="text-slate-400">Within:</span>
            {[5, 10, 15, 25, 50].map(mi => (
              <button
                key={mi}
                type="button"
                onClick={() => setRadiusFilter(radiusFilter === mi ? null : mi)}
                className={`px-2 py-1 rounded-full border font-semibold transition ${radiusFilter === mi ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300'}`}
                data-testid={`applicant-radius-${mi}`}
              >
                {mi} mi
              </button>
            ))}
            {radiusFilter && (
              <button type="button" onClick={() => setRadiusFilter(null)} className="text-slate-500 underline" data-testid="applicant-radius-clear">
                clear
              </button>
            )}
          </div>
        )}

        <div
          className="flex-1 min-h-0 overflow-y-auto overscroll-contain"
          style={{ WebkitOverflowScrolling: 'touch' }}
          data-testid="applicant-drawer-list"
        >
          {loading ? (
            <div className="py-20 flex items-center justify-center text-gray-500 text-sm">
              <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading…
            </div>
          ) : displayList.length === 0 ? (
            <div className="py-20 text-center text-gray-500 text-sm" data-testid="applicant-drawer-empty">
              {radiusFilter ? `No applicants within ${radiusFilter} mi of the venue.` : 'No pending applicants.'}
            </div>
          ) : (
            <ul className="divide-y divide-gray-100">
              {displayList.map((a, idx) => {
                if (!a || typeof a !== 'object') return null;
                const talentId = a.talent_id || a.user_id || `unknown-${idx}`;
                const rowKey = a.application_id || talentId || `row-${idx}`;
                const name = a.name || a.display_name || 'Unnamed applicant';
                const initials = (name || '?').slice(0, 2).toUpperCase();
                const cityState = [a.city, a.state].filter(Boolean).join(', ');
                const roleName = a.role_name || a.role || null;
                const reliability =
                  typeof a.reliability_score === 'number' && Number.isFinite(a.reliability_score)
                    ? a.reliability_score
                    : null;
                const status = a.status || 'pending';
                const badges = Array.isArray(a.badges) ? a.badges : [];
                const isStandby = a.is_standby === true;
                const isBackup = a.is_backup === true;
                const isBooked = !!a.booking_id;
                // Iter-315a — Prefer the lifecycle_stage the backend
                // computes (Applied vs Confirmed vs Checked In vs …)
                // over the raw applications.status ("accepted") which
                // was confusing admins when talent who applied got
                // added to a shift.
                const lifecycleStage = a.lifecycle_stage || (isBooked ? 'confirmed' : (status === 'pending' ? 'applied' : status));
                const stageLabelMap = {
                  applied: 'Applied',
                  invited: 'Invited',
                  accepted: 'Accepted',
                  confirmed: 'Confirmed',
                  checked_in: 'Checked In',
                  completed: 'Checked Out',
                  paid: 'Paid',
                };
                const stageLabel = stageLabelMap[lifecycleStage] || String(lifecycleStage).replace(/_/g, ' ');
                const stageColorMap = {
                  applied: 'bg-slate-50 text-slate-700 border-slate-200',
                  invited: 'bg-cyan-50 text-cyan-700 border-cyan-200',
                  accepted: 'bg-blue-50 text-blue-700 border-blue-200',
                  confirmed: 'bg-emerald-50 text-emerald-700 border-emerald-200',
                  checked_in: 'bg-violet-50 text-violet-700 border-violet-200',
                  completed: 'bg-purple-50 text-purple-700 border-purple-200',
                  paid: 'bg-emerald-100 text-emerald-800 border-emerald-300',
                };
                const stageClass = stageColorMap[lifecycleStage] || 'bg-indigo-50 text-indigo-700 border-indigo-200';

                return (
                  <li key={rowKey} className="px-4 py-3" data-testid={`applicant-row-${talentId}`}>
                    <div className="flex items-start gap-3">
                      {(() => {
                        const halo = HaloRing.computeFromBooking(a);
                        return (
                      <HaloRing state={halo.state} reason={halo.reason} size="sm" testIdSuffix={`applicant-${talentId}`}>
                      {a.profile_photo_url ? (
                        <button
                          type="button"
                          onClick={() => talentId && openProfile?.(talentId, { surface: 'applicant_list' })}
                          className="w-10 h-10 rounded-full overflow-hidden border border-white shrink-0"
                          data-testid={`applicant-photo-btn-${talentId}`}
                          title={`View ${name}'s profile`}
                        >
                          <img
                            src={a.profile_photo_url}
                            alt=""
                            className="w-full h-full object-cover"
                            onError={(e) => { e.currentTarget.style.display = 'none'; }}
                          />
                        </button>
                      ) : (
                        <button
                          type="button"
                          onClick={() => talentId && openProfile?.(talentId, { surface: 'applicant_list' })}
                          className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center text-xs font-semibold text-gray-600 shrink-0 border border-white"
                          data-testid={`applicant-photo-btn-${talentId}`}
                          title={`View ${name}'s profile`}
                        >
                          {initials}
                        </button>
                      )}
                      </HaloRing>
                        );
                      })()}

                      <div className="min-w-0 flex-1">
                        <button
                          type="button"
                          onClick={() => talentId && openProfile?.(talentId, { surface: 'applicant_list' })}
                          className="font-medium text-gray-900 truncate text-left hover:text-cyan-700 hover:underline block w-full"
                          title={name}
                          data-testid={`applicant-name-btn-${talentId}`}
                        >
                          {name}
                        </button>
                        <div className="text-xs text-gray-500 mt-0.5 flex items-center gap-2 flex-wrap">
                          {cityState && (
                            <span className="inline-flex items-center gap-1">
                              <MapPin className="w-3 h-3" />
                              {cityState}
                            </span>
                          )}
                          {a.distance_available && a.distance_miles != null && (
                            <span className="inline-flex items-center gap-1 font-semibold text-indigo-600" data-testid={`applicant-distance-${talentId}`}>
                              {a.distance_approx ? '~' : ''}{a.distance_miles} mi from venue
                            </span>
                          )}
                          {roleName && (
                            <span className="inline-flex items-center gap-1 text-gray-600">
                              · {roleName}
                            </span>
                          )}
                          {reliability !== null && (
                            <span className="inline-flex items-center gap-0.5 text-amber-600">
                              <Star className="w-3 h-3 fill-amber-500 text-amber-500" />
                              {reliability.toFixed(1)}
                            </span>
                          )}
                        </div>
                        <div className="mt-1 inline-flex items-center gap-1.5 flex-wrap">
                          <Badge
                            className={`${stageClass} border text-[10px] uppercase tracking-wide`}
                            data-testid={`applicant-stage-badge-${talentId}`}
                          >
                            {stageLabel}
                          </Badge>
                          {a.ggf_certified && (
                            <GgfBadge
                              certified
                              score={a.ggf_test_score}
                              date={a.ggf_certified_at}
                              source={a.ggf_certified_source}
                              size="xs"
                              showScore
                              context={{ isAdmin, isGgfClient }}
                            />
                          )}
                          {isStandby && (
                            <Badge
                              className="bg-amber-50 text-amber-700 border border-amber-200 text-[10px] uppercase tracking-wide"
                              data-testid={`applicant-standby-badge-${talentId}`}
                            >
                              Standby
                            </Badge>
                          )}
                          {isBackup && (
                            <Badge
                              className="bg-purple-50 text-purple-700 border border-purple-200 text-[10px] uppercase tracking-wide"
                              data-testid={`applicant-backup-badge-${talentId}`}
                            >
                              Backup
                            </Badge>
                          )}
                          {isBooked && (
                            <Badge
                              className="bg-emerald-50 text-emerald-700 border border-emerald-200 text-[10px] uppercase tracking-wide"
                              data-testid={`applicant-booked-badge-${talentId}`}
                            >
                              Booked
                            </Badge>
                          )}
                          {badges.length > 0 && (
                            <BadgeRow badges={badges} max={3} />
                          )}
                        </div>
                        {/* Iter-202 — verified certifications surfaced inline
                            for bartender / regulated-role compliance checks.
                            Iter-227n — admins can approve/reject inline. */}
                        <div className="mt-1.5" data-testid={`applicant-certs-${talentId}`}>
                          <CertificationList
                            certifications={a.certifications}
                            variant="inline"
                            showDocLink={isAdmin}
                            adminMode={isAdmin}
                            onReviewed={() => load()}
                            testIdPrefix={`applicant-cert-${talentId}`}
                            emptyMessage="No certifications uploaded"
                          />
                        </div>
                        {/* Iter-315a — Lifecycle audit trail. Clarifies
                            Applied vs Invited vs Accepted vs Confirmed
                            with who/when for each transition. */}
                        <div className="mt-2" data-testid={`applicant-lifecycle-${talentId}`}>
                          <LifecycleTimeline
                            shift_id={shiftId}
                            talent_id={talentId}
                            booking_id={a.booking_id || null}
                          />
                        </div>
                      </div>

                      <div className="flex flex-col gap-1.5 shrink-0">
                        {!isBooked && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => quickBook(talentId)}
                            disabled={bookingId === talentId || !talentId}
                            className="border-emerald-400 text-emerald-700 hover:bg-emerald-50"
                            data-testid={`quick-book-btn-${talentId}`}
                            title="Assign as primary"
                          >
                            {bookingId === talentId
                              ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                              : <><UserPlus className="w-3.5 h-3.5 mr-1" /> Quick Book</>}
                          </Button>
                        )}
                        {isAdmin && talentId && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => openInviteToShift?.(talentId, name)}
                            className="border-cyan-400 text-cyan-700 hover:bg-cyan-50"
                            data-testid={`invite-to-gig-applicant-${talentId}`}
                            title="Invite to another gig"
                          >
                            <Send className="w-3.5 h-3.5 mr-1" /> Invite
                          </Button>
                        )}
                        {isAdmin && talentId && (
                          <AddToInviteCartButton
                            talent={{
                              user_id: talentId,
                              name,
                              photo_url: a.profile_photo_url,
                              role: roleName,
                              city: a.city,
                              state: a.state,
                            }}
                            variant="icon"
                            testIdSuffix={`applicant-${talentId}`}
                          />
                        )}
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <footer className="px-4 py-3 border-t border-gray-200 bg-gray-50 text-xs text-gray-500">
          {safeList.length} pending · applicants are not yet assigned
        </footer>
      </aside>
    </div>
  );
};

export { DrawerInlineError };
export default ApplicantDrawer;
